// hooks/useLoadProjects.ts
import { useCallback, useRef } from "react";
import httpClient, { AuthNotReadyError } from "../utils/httpClient";
import { ProjectsProps } from "../providers/types";
import { idbGet, idbSet } from "../utils/idbCache";
import { sleep } from "../utils/sleep";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2000;

export const useLoadProjects = (
  setProjects: (data: ProjectsProps[]) => void
) => {
  const inFlightRef = useRef(false);

  const loadProjects = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    // Hydrate from cache immediately
    try {
      const cached = await idbGet<ProjectsProps[]>("cba.projects");
      if (Array.isArray(cached) && cached.length > 0) setProjects(cached);
    } catch {
      // ignore cache read errors
    }

    const fetchOnce = async (): Promise<ProjectsProps[] | null> => {
      try {
        const res = await httpClient.get<ProjectsProps[]>("/api/cba/projects");
        return Array.isArray(res.data)
          ? res.data.sort((a, b) => a.subdivision.localeCompare(b.subdivision))
          : null;
      } catch (e) {
        if (e instanceof AuthNotReadyError || (e as any)?.response?.status === 401) {
          return null; // Silent retry
        }
        throw e; // Real error
      }
    };

    try {
      let data = await fetchOnce();

      let attempts = 0;
      while (data === null && attempts < MAX_RETRIES) {
        await sleep(RETRY_DELAY_MS);
        data = await fetchOnce();
        attempts++;
      }

      if (data === null) return; // Silently keep cached data

      setProjects(data);

      try {
        await idbSet("cba.projects", data);
      } catch (e) {
        console.warn("Caching projects to IndexedDB failed.", e);
      }
    } catch (e) {
      console.error("Error loading projects:", e);
    } finally {
      inFlightRef.current = false;
    }
  }, [setProjects]);

  return { loadProjects };
};